import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import '@fontsource/plus-jakarta-sans/400.css';
import '@fontsource/plus-jakarta-sans/600.css';
import '@fontsource/plus-jakarta-sans/800.css';
import '@phosphor-icons/web/duotone';
import '@phosphor-icons/web/bold';
import './styles/tokens.css';
import './styles/global.css';
import { loadConfig, ConfigError, NoFunnelSelected, funnelPath } from './engine/loadConfig.ts';
import { setAssetManifest, type AssetEntry } from './engine/assets.ts';
import type { Config } from './engine/types.ts';
import { Funnel } from './pages/Funnel.tsx';
import { Kit } from './pages/Kit.tsx';
import { followVisualViewport } from './shell/viewport.ts';
import { firstTouch } from './engine/session.ts';
import { CORAL_DEEP, CREAM, INK, MUTED, PAPER } from './ui/theme.ts';

function ErrorPage({ error }: { error: Error }) {
  const detail = error instanceof ConfigError ? error.detail : [];
  return (
    <div style={{ maxWidth: 520, margin: '10vh auto', padding: 24, background: PAPER, borderRadius: 24, fontFamily: 'inherit' }}>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: CORAL_DEEP }}>Config error</div>
      <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.03em', color: INK }}>{error.message}</h1>
      {detail.length ? (
        <ul style={{ color: MUTED, fontSize: 14, lineHeight: 1.6 }}>
          {detail.map((d) => (
            <li key={d}>
              <code>{d}</code>
            </li>
          ))}
        </ul>
      ) : null}
      <p style={{ color: MUTED, fontSize: 14 }}>
        Fix the JSON under <code>public/config</code> and reload. <code>npm run validate</code> reports the same list in CI.
      </p>
    </div>
  );
}

/**
 * Every session shows which funnel and language it is on: /<code>/<lang>.
 * A bare "/" and the old ?f=&lang= links both become that, rewritten in place —
 * never a redirect, which would drop the referrer and restart the session —
 * keeping every attribution param and the current step hash.
 */
function canonicalise(code: string, lang: string): void {
  if (location.pathname.startsWith('/kit')) return;
  const params = new URLSearchParams(location.search);
  params.delete('f');
  params.delete('lang');
  const path = funnelPath(code, lang);
  const query = params.toString();
  const next = `${path}${query ? `?${query}` : ''}${location.hash}`;
  if (`${location.pathname}${location.search}${location.hash}` !== next) history.replaceState(null, '', next);
}

/** the bare host: no funnel named, so nothing to run */
function NoFunnel() {
  return (
    <div style={{ maxWidth: 520, margin: '18vh auto', padding: 24, textAlign: 'center', fontFamily: 'inherit' }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em', color: INK, margin: 0 }}>Nothing to see here</h1>
      <p style={{ color: MUTED, fontSize: 14, marginTop: 8 }}>Open the link you were sent.</p>
    </div>
  );
}

function App() {
  const [config, setConfig] = useState<Config | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const kit = new URLSearchParams(location.search).has('kit') || location.pathname.startsWith('/kit');

  useEffect(() => {
    loadConfig()
      .then(async (c) => {
        const manifest = await fetch('/config/assets.manifest.json')
          .then((r) => (r.ok ? (r.json() as Promise<Record<string, AssetEntry>>) : undefined))
          .catch(() => undefined);
        setAssetManifest(manifest, c.settings.assetsVersion);
        document.documentElement.lang = c.lang;
        // the first touch is read from the URL before it is rewritten: `f` is
        // the ad angle and would otherwise be gone by the time the funnel mounts
        firstTouch(location.search, { funnel_code: c.funnel.code, lang: c.lang });
        canonicalise(c.funnel.code, c.lang);
        setConfig(c);
      })
      .catch((e: Error) => {
        setError(e);
        console.error(e);
      });
  }, []);

  if (error instanceof NoFunnelSelected) return <NoFunnel />;
  if (error) return <ErrorPage error={error} />;
  if (!config) return <div style={{ minHeight: '100vh', background: CREAM }} aria-busy="true" />;
  return kit ? <Kit config={config} /> : <Funnel config={config} />;
}

// iOS keeps a page's old scroll offset across visits and applies it even to a
// page that no longer scrolls, which shifts the whole fixed shell upwards.
try {
  history.scrollRestoration = 'manual';
} catch {
  /* not supported */
}
window.scrollTo(0, 0);
addEventListener('pageshow', () => window.scrollTo(0, 0));
followVisualViewport();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
